import { ByteArrayUtils } from "../Utils/ByteArrayUtils";

export class MacAddress {
	/// <summary>
	/// Creates a MacAddress object from the specified address string.
	/// The MAC address string must have one of the following forms:
	///		"0123456789ab"
	///		"01:23:45:67:89:ab"
	///		"01-23-45-67-89-ab"
	///	If the string contains colons or dashes, a zero byte can also
	///	be represented by a single zero (i.e., compact form).
	/// </summary>
	/// <param name="ipString">MAC address string</param>
	/// <returns>New MacAddress object, or null if macString is invalid</returns>
	public static CreateFromString(macString: string): MacAddress {
		if (!macString)
			return null;

		var hasColon = false;
		var hasDash = false;
		for (var i = 0; i < macString.length; i++) {
			var c = macString.charAt(i);
			
			if (c == ':') {
				hasColon = true;
				break;
			}
			else if (c == '-') {
				hasDash = true;
				break;
			}
		}

		var bytes = new Uint8Array(6);

		if (hasColon || hasDash) {
			var separator = (hasColon ? ':' : '-');
			var segments = macString.split(separator);
			if (segments.length != bytes.length)
				return null;
			for (var i = 0; i < bytes.length; ++i) {
				bytes[i] = parseInt(segments[i], 16);
			}
		}
		else {
			if (macString.length != bytes.length * 2)
				return null;

			for (var i = 0, j = 0; i < bytes.length; ++i, j += 2) {
				bytes[i] = parseInt(macString.substr(j, 2), 16);
			}
		}

		return new MacAddress(bytes);
	}

	public static Create(bytes: Uint8Array): MacAddress {
		if (bytes == null || bytes.length != 6)
			return null;

		return new MacAddress(bytes);
	}

	public static CreateNullAddress():MacAddress {
		return MacAddress.CreateFromString("000000000000");
	}

	private _bytes: Uint8Array;

	public get VendorPrefix(): string {
		var OuiLength = 6;

		var vendorPrefix = '';

		var macStr = this.ToFormattedString(false, "");
		if (macStr.length >= OuiLength)
			vendorPrefix = macStr.substring(0, OuiLength);

		return (vendorPrefix);
	}

	constructor(bytes: Uint8Array){
		this._bytes = bytes;
	}

	public Clone(): MacAddress {
		var bytes = new Uint8Array(this._bytes.length);
		for (var i = 0; i < this._bytes.length;i++) {
			bytes[i] = this._bytes[i];
		}
		return new MacAddress(bytes);
	}

	public Equals(obj: any): boolean {
		if (obj == null)
			return false;
		if (!(obj instanceof MacAddress))
			return false;
		var other = <MacAddress>obj;
		if (this._bytes.length != other._bytes.length)
			return false;
		return (ByteArrayUtils.Compare(this._bytes, other._bytes) == 0);
	}

	public GetHashCode() : number {
		return ByteArrayUtils.ComputeHashCode(this._bytes);
	}

	public toString(): string {
		return this.ToFormattedString(false, "");
	}

	public ToString(): string {
		return this.toString();
	}

	public ToFormattedString(upperCase: boolean = true, separator: string = ':'): string {
		var result = "";
		for (var i = 0; i < this._bytes.length; ++i) {
			result += ('00' + this._bytes[i].toString(16)).substr(-2);
				
			if (i < this._bytes.length - 1)
				result += separator;
		}

		if (upperCase)
			return result.toLocaleUpperCase();
		else
			return result;
	}

	public CompareTo(other: MacAddress): number {
		if (other == null)
			return 1;
		else if (this._bytes.length < other._bytes.length)
			return -1;
		else if (this._bytes.length > other._bytes.length)
			return 1;
		else
			return ByteArrayUtils.Compare(this._bytes, other._bytes);
	}

	public Increment(): boolean {
		return ByteArrayUtils.Increment(this._bytes);
	}

	public Decrement(): boolean {
		return ByteArrayUtils.Decrement(this._bytes);
	}

	public get IsNull(): boolean {
		if ((!this._bytes) || (this._bytes.length <= 0))
			return true;

		var isNull = true;

		for (var i = 0; i < this._bytes.length;i++) {
			var macByte = this._bytes[i];
			if (macByte == 0)
				continue;

			isNull = false;
			break;
		}

		return (isNull);
	}


	public get IsValidAddress() : boolean {
		if (this.IsNull)
			return false;

		var isValid = true;
		var nonZeroByteCount = 0;
		var non255ByteCount = 0;

		for (var i = 0; i < this._bytes.length; i++) {
			var macByte = this._bytes[i];
			if (macByte != 255)
				non255ByteCount++;
			if (macByte != 0)
				nonZeroByteCount++;
		}

		if (nonZeroByteCount <= 1)
			isValid = false;

		if (non255ByteCount <= 0)
			isValid = false;

		return isValid;
	}

	public GetByte(index: number): number {
		var macByte = 0;

		if ((this._bytes) && (this._bytes.length > 0)) {
			if (index <= this._bytes.length)
				macByte = this._bytes[index];
		}

		return macByte;
	}
}
